import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Phone, Plus, Search, Filter, Download, RefreshCw, Calendar,
  User, MapPin, Mail, FileText, Clock, CheckCircle2, XCircle,
  PhoneCall, PhoneMissed, PhoneOff, ChevronRight, Eye, Edit, Trash2,
  TrendingUp, Users, BarChart3, PieChart, Check, ChevronsUpDown
} from "lucide-react";
import { ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";

export default function ControlLlamadasPage() {
  const { user, getAuthHeaders } = useAuth();
  const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

  // State
  const [llamadas, setLlamadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clientePopoverOpen, setClientePopoverOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState("");
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedLlamada, setSelectedLlamada] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterResultado, setFilterResultado] = useState("");
  const [filterFechaInicio, setFilterFechaInicio] = useState("");
  const [filterFechaFin, setFilterFechaFin] = useState("");
  const [activeTimeFilter, setActiveTimeFilter] = useState(""); // "hoy", "semana", "mes", "seguimientos"
  
  // Form state
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nombre: "",
    cliente_telefono: "",
    cliente_celular: "",
    cliente_email: "",
    cliente_cedula: "",
    cliente_ciudad: "",
    cliente_direccion: "",
    poliza: "",
    fecha_llamada: new Date().toISOString().slice(0, 16),
    resultado: "",
    proxima_accion: "",
    fecha_proxima_accion: "",
    validaciones: [],
    observaciones: "",
    datos_actualizar: ""
  });

  // Validation prompt state
  const [validacionPrompt, setValidacionPrompt] = useState(null); // { id, label, step, response }
  const [promptInput, setPromptInput] = useState("");

  // Fetch data
  const fetchLlamadas = useCallback(async () => {
    try {
      let url = `${API_URL}/api/control-llamadas/?limit=100`;
      if (searchTerm) url += `&busqueda=${encodeURIComponent(searchTerm)}`;
      if (filterResultado) url += `&resultado=${encodeURIComponent(filterResultado)}`;
      if (filterFechaInicio) url += `&fecha_inicio=${filterFechaInicio}`;
      if (filterFechaFin) url += `&fecha_fin=${filterFechaFin}`;
      
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setLlamadas(data.items || []);
    } catch (error) {
      console.error("Error fetching llamadas:", error);
      toast.error("Error al cargar las llamadas");
    }
  }, [API_URL, getAuthHeaders, searchTerm, filterResultado, filterFechaInicio, filterFechaFin]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/control-llamadas/dashboard`, getAuthHeaders());
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/control-llamadas/config/all`, getAuthHeaders());
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchClientes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/mensajeria/clientes`, getAuthHeaders());
      const data = await res.json();
      setClientes(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error("Error fetching clientes:", error);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLlamadas(), fetchDashboard(), fetchConfig(), fetchClientes()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLlamadas, fetchDashboard, fetchConfig, fetchClientes]);

  // Handle client selection - Only fills the name, user can manually enter other data
  const handleClienteSelect = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    if (cliente) {
      setFormData(prev => ({
        ...prev,
        cliente_id: cliente.id,
        cliente_nombre: cliente.nombre || cliente.name || "",
        cliente_telefono: cliente.telefono || prev.cliente_telefono || "",
        cliente_celular: cliente.celular || prev.cliente_celular || "",
        cliente_email: cliente.email || prev.cliente_email || "",
        cliente_direccion: cliente.direccion || prev.cliente_direccion || ""
      }));
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.cliente_nombre || !formData.resultado) {
      toast.error("Por favor complete los campos requeridos");
      return;
    }
    if (!formData.datos_actualizar?.trim()) {
      toast.error("El campo 'Datos a Actualizar' es obligatorio");
      return;
    }

    try {
      const url = editMode 
        ? `${API_URL}/api/control-llamadas/${selectedLlamada.id}`
        : `${API_URL}/api/control-llamadas/`;
      
      const res = await fetch(url, {
        method: editMode ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editMode ? "Llamada actualizada" : "Llamada registrada");
        setShowNewDialog(false);
        resetForm();
        fetchLlamadas();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al guardar la llamada");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta llamada?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/control-llamadas/${id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Llamada eliminada");
        fetchLlamadas();
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleExport = async () => {
    try {
      let url = `${API_URL}/api/control-llamadas/export/excel?`;
      if (filterResultado) url += `&resultado=${encodeURIComponent(filterResultado)}`;
      if (filterFechaInicio) url += `&fecha_inicio=${filterFechaInicio}`;
      if (filterFechaFin) url += `&fecha_fin=${filterFechaFin}`;
      
      const res = await fetch(url, getAuthHeaders());
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "control_llamadas.xlsx";
      a.click();
      toast.success("Archivo exportado");
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nombre: "",
      cliente_telefono: "",
      cliente_celular: "",
      cliente_email: "",
      cliente_cedula: "",
      cliente_ciudad: "",
      cliente_direccion: "",
      poliza: "",
      fecha_llamada: new Date().toISOString().slice(0, 16),
      resultado: "",
      proxima_accion: "",
      fecha_proxima_accion: "",
      validaciones: [],
      observaciones: "",
      datos_actualizar: ""
    });
    setEditMode(false);
    setSelectedLlamada(null);
  };

  const openEditDialog = (llamada) => {
    setSelectedLlamada(llamada);
    setFormData({
      cliente_id: llamada.cliente_id || "",
      cliente_nombre: llamada.cliente_nombre || "",
      cliente_telefono: llamada.cliente_telefono || "",
      cliente_celular: llamada.cliente_celular || "",
      cliente_email: llamada.cliente_email || "",
      cliente_cedula: llamada.cliente_cedula || "",
      cliente_ciudad: llamada.cliente_ciudad || "",
      cliente_direccion: llamada.cliente_direccion || "",
      poliza: llamada.poliza || "",
      fecha_llamada: llamada.fecha_llamada?.slice(0, 16) || "",
      resultado: llamada.resultado || "",
      proxima_accion: llamada.proxima_accion || "",
      fecha_proxima_accion: llamada.fecha_proxima_accion?.slice(0, 16) || "",
      validaciones: llamada.validaciones || [],
      observaciones: llamada.observaciones || "",
      datos_actualizar: llamada.datos_actualizar || ""
    });
    setEditMode(true);
    setShowNewDialog(true);
  };

  // Validation prompt logic
  const getValidacionPrompt = (tipoId, label) => {
    const lower = tipoId.toLowerCase();
    if (["telefono", "celular", "email", "direccion", "contacto", "cedula"].includes(lower)) {
      return { question: `¿El campo "${label}" necesita actualización?`, type: "yes_no_then_input", inputLabel: `Ingrese el nuevo ${label}:` };
    }
    if (lower.includes("marbete")) {
      return { question: "¿El cliente recibió el marbete?", type: "yes_no_only" };
    }
    if (lower.includes("valor") || lower.includes("asegurad")) {
      return { question: "¿El cliente estuvo de acuerdo con el valor asegurado?", type: "agree_disagree", inputLabel: "Ingrese el nuevo valor asegurado:" };
    }
    return null;
  };

  const handleValidacionCheck = (tipoId, tipoLabel, checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, validaciones: [...prev.validaciones, tipoId] }));
      const prompt = getValidacionPrompt(tipoId, tipoLabel);
      if (prompt) {
        setValidacionPrompt({ id: tipoId, label: tipoLabel, step: "initial", ...prompt });
        setPromptInput("");
      }
    } else {
      setFormData(prev => ({ ...prev, validaciones: prev.validaciones.filter(v => v !== tipoId) }));
    }
  };

  const appendDatosActualizar = (text) => {
    setFormData(prev => ({
      ...prev,
      datos_actualizar: prev.datos_actualizar ? `${prev.datos_actualizar}\n${text}` : text
    }));
  };

  const handlePromptResponse = (response) => {
    if (!validacionPrompt) return;
    const { id, label, type } = validacionPrompt;

    if (type === "yes_no_then_input") {
      if (validacionPrompt.step === "initial") {
        if (response === "si") {
          setValidacionPrompt(prev => ({ ...prev, step: "input" }));
          setPromptInput("");
        } else {
          appendDatosActualizar(`${label}: Sin actualización necesaria`);
          setValidacionPrompt(null);
        }
      } else if (validacionPrompt.step === "input") {
        if (promptInput.trim()) {
          appendDatosActualizar(`${label}: ${promptInput.trim()}`);
        }
        setValidacionPrompt(null);
      }
    } else if (type === "yes_no_only") {
      appendDatosActualizar(`${label}: ${response === "si" ? "Sí, recibido" : "No recibido"}`);
      setValidacionPrompt(null);
    } else if (type === "agree_disagree") {
      if (validacionPrompt.step === "initial") {
        if (response === "si") {
          appendDatosActualizar(`${label}: Cliente de acuerdo`);
          setValidacionPrompt(null);
        } else {
          setValidacionPrompt(prev => ({ ...prev, step: "input" }));
          setPromptInput("");
        }
      } else if (validacionPrompt.step === "input") {
        if (promptInput.trim()) {
          appendDatosActualizar(`${label}: Nuevo valor → ${promptInput.trim()}`);
        }
        setValidacionPrompt(null);
      }
    }
  };

  const getResultadoColor = (resultado) => {
    const colors = {
      "Contactado": "bg-green-100 text-green-800",
      "No Contesta": "bg-yellow-100 text-yellow-800",
      "Número Equivocado": "bg-red-100 text-red-800",
      "Buzón de Voz": "bg-blue-100 text-blue-800",
      "Ocupado": "bg-orange-100 text-orange-800",
      "Fuera de Servicio": "bg-gray-100 text-gray-800"
    };
    return colors[resultado] || "bg-slate-100 text-slate-800";
  };

  const getResultadoIcon = (resultado) => {
    if (resultado === "Contactado") return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    if (resultado === "No Contesta") return <PhoneMissed className="w-4 h-4 text-yellow-600" />;
    if (resultado === "Número Equivocado") return <XCircle className="w-4 h-4 text-red-600" />;
    return <PhoneOff className="w-4 h-4 text-gray-600" />;
  };

  // Handle time filter from dashboard cards
  const handleTimeFilter = (filterType) => {
    // Toggle filter if clicking on same card
    if (activeTimeFilter === filterType) {
      setActiveTimeFilter("");
      setFilterFechaInicio("");
      setFilterFechaFin("");
      return;
    }

    setActiveTimeFilter(filterType);
    const now = new Date();
    
    switch (filterType) {
      case "hoy":
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setFilterFechaInicio(todayStart.toISOString().slice(0, 10));
        setFilterFechaFin(todayStart.toISOString().slice(0, 10));
        break;
      case "semana":
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        setFilterFechaInicio(weekStart.toISOString().slice(0, 10));
        setFilterFechaFin(now.toISOString().slice(0, 10));
        break;
      case "mes":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        setFilterFechaInicio(monthStart.toISOString().slice(0, 10));
        setFilterFechaFin(now.toISOString().slice(0, 10));
        break;
      case "seguimientos":
        // Filter for records with proxima_accion set
        setFilterFechaInicio("");
        setFilterFechaFin("");
        break;
      default:
        setFilterFechaInicio("");
        setFilterFechaFin("");
    }
  };

  // Filter llamadas based on active filters
  const filteredLlamadas = llamadas.filter(llamada => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        llamada.cliente_nombre?.toLowerCase().includes(search) ||
        llamada.cliente_telefono?.includes(search) ||
        llamada.cliente_cedula?.includes(search) ||
        llamada.poliza?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Resultado filter
    if (filterResultado && llamada.resultado !== filterResultado) return false;
    
    // Special filter for seguimientos
    if (activeTimeFilter === "seguimientos") {
      return llamada.proxima_accion && llamada.proxima_accion !== "";
    }
    
    return true;
  });

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-0 md:pt-0 overflow-x-clip" data-testid="control-llamadas-page">
        {/* STICKY HEADER - se mantiene fijo al hacer scroll */}
        <div className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900 -mx-4 md:-mx-6 px-4 md:px-6 pt-14 md:pt-6 pb-4 border-b border-slate-200 dark:border-slate-700" data-testid="control-llamadas-sticky-header">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-600 rounded-xl">
              <Phone className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Control de Llamadas</h1>
              <p className="text-sm text-slate-500">Validación de datos de clientes</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchLlamadas(); fetchDashboard(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={() => { resetForm(); setShowNewDialog(true); }} data-testid="new-llamada-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Llamada
            </Button>
          </div>
        </div>

        {/* Dashboard Cards - Clickable for filtering */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card 
              className={`bg-gradient-to-br from-indigo-500 to-indigo-600 text-white cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeTimeFilter === "" && !filterFechaInicio ? "ring-2 ring-white ring-opacity-50" : ""}`}
              onClick={() => { setActiveTimeFilter(""); setFilterFechaInicio(""); setFilterFechaFin(""); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-100 text-sm">Total Llamadas</p>
                    <p className="text-3xl font-bold">{dashboard.total_llamadas}</p>
                  </div>
                  <PhoneCall className="w-10 h-10 text-indigo-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeTimeFilter === "hoy" ? "ring-2 ring-white ring-opacity-50" : ""}`}
              onClick={() => handleTimeFilter("hoy")}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Hoy</p>
                    <p className="text-3xl font-bold">{dashboard.llamadas_hoy}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeTimeFilter === "semana" ? "ring-2 ring-white ring-opacity-50" : ""}`}
              onClick={() => handleTimeFilter("semana")}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Esta Semana</p>
                    <p className="text-3xl font-bold">{dashboard.llamadas_semana}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeTimeFilter === "mes" ? "ring-2 ring-white ring-opacity-50" : ""}`}
              onClick={() => handleTimeFilter("mes")}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Este Mes</p>
                    <p className="text-3xl font-bold">{dashboard.llamadas_mes}</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeTimeFilter === "seguimientos" ? "ring-2 ring-white ring-opacity-50" : ""}`}
              onClick={() => handleTimeFilter("seguimientos")}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Seguimientos</p>
                    <p className="text-3xl font-bold">{dashboard.pendientes_seguimiento}</p>
                  </div>
                  <Clock className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Row */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* By Result */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  Por Resultado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.por_resultado?.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{item.resultado || "Sin resultado"}</span>
                      <Badge variant="secondary">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By User */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Por Usuario
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.por_usuario?.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 truncate">{item.usuario || "Sin usuario"}</span>
                      <Badge variant="secondary">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Validation */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Validaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.por_validacion?.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 capitalize">{item.validacion}</span>
                      <Badge variant="secondary">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por cliente, teléfono, cédula o póliza..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterResultado || "all"} onValueChange={(v) => setFilterResultado(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Resultado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {config?.resultados?.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={filterFechaInicio}
                onChange={(e) => setFilterFechaInicio(e.target.value)}
                className="w-[150px]"
                placeholder="Desde"
              />
              <Input
                type="date"
                value={filterFechaFin}
                onChange={(e) => setFilterFechaFin(e.target.value)}
                className="w-[150px]"
                placeholder="Hasta"
              />
              <Button variant="outline" onClick={() => { setSearchTerm(""); setFilterResultado(""); setFilterFechaInicio(""); setFilterFechaFin(""); }}>
                Limpiar
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
        {/* FIN STICKY HEADER */}

        {/* Table */}
        <Card className="mt-4">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Teléfono</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Póliza</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Resultado</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Validaciones</th>
                    <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase">Usuario</th>
                    <th className="p-4 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        Cargando...
                      </td>
                    </tr>
                  ) : filteredLlamadas.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        {activeTimeFilter ? `No hay llamadas para el filtro "${activeTimeFilter}"` : "No hay llamadas registradas"}
                      </td>
                    </tr>
                  ) : (
                    filteredLlamadas.map((llamada) => (
                      <tr key={llamada.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="p-4 text-sm">
                          {llamada.fecha_llamada ? new Date(llamada.fecha_llamada).toLocaleDateString('es-DO') : "-"}
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-slate-900 dark:text-white">{llamada.cliente_nombre}</div>
                          <div className="text-xs text-slate-500">{llamada.cliente_cedula}</div>
                        </td>
                        <td className="p-4 text-sm">{llamada.cliente_telefono || llamada.cliente_celular || "-"}</td>
                        <td className="p-4 text-sm">{llamada.poliza || "-"}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getResultadoIcon(llamada.resultado)}
                            <Badge className={getResultadoColor(llamada.resultado)}>
                              {llamada.resultado}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {llamada.validaciones?.slice(0, 3).map((v) => (
                              <Badge key={v} variant="outline" className="text-xs capitalize">{v}</Badge>
                            ))}
                            {llamada.validaciones?.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{llamada.validaciones.length - 3}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600">{llamada.usuario_nombre}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedLlamada(llamada); setShowViewDialog(true); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(llamada)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(llamada.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* New/Edit Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {editMode ? "Editar Llamada" : "Nueva Llamada"}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Client Selection */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Datos del Cliente
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Seleccionar Cliente (Opcional)</Label>
                      <Popover open={clientePopoverOpen} onOpenChange={setClientePopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={clientePopoverOpen}
                            className="w-full justify-between font-normal"
                            data-testid="llamadas-cliente-combobox-trigger"
                          >
                            <span className="truncate text-left">
                              {(() => {
                                const sel = clientes.find(c => c.id === formData.cliente_id);
                                if (sel) return `${sel.nombre || sel.name} - ${sel.cedula || sel.rnc || "Sin cédula"}`;
                                return <span className="text-slate-400">Buscar cliente existente...</span>;
                              })()}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[640px]" align="start">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Escribe nombre, cédula, RNC, teléfono o email..."
                              value={clienteSearch}
                              onValueChange={setClienteSearch}
                              data-testid="llamadas-cliente-search-input"
                            />
                            <CommandList>
                              <CommandEmpty>No se encontró ningún cliente.</CommandEmpty>
                              <CommandGroup>
                                {(() => {
                                  const q = (clienteSearch || "").trim().toLowerCase();
                                  const norm = (v) => (v == null ? "" : String(v)).toLowerCase();
                                  const filtered = q
                                    ? clientes.filter(c =>
                                        norm(c.nombre).includes(q) ||
                                        norm(c.name).includes(q) ||
                                        norm(c.cedula).includes(q) ||
                                        norm(c.rnc).includes(q) ||
                                        norm(c.telefono).includes(q) ||
                                        norm(c.celular).includes(q) ||
                                        norm(c.email).includes(q)
                                      )
                                    : clientes;
                                  return filtered.slice(0, 200).map((c) => (
                                    <CommandItem
                                      key={c.id}
                                      value={c.id}
                                      onSelect={() => {
                                        handleClienteSelect(c.id);
                                        setClientePopoverOpen(false);
                                        setClienteSearch("");
                                      }}
                                      data-testid={`llamadas-cliente-option-${c.id}`}
                                    >
                                      <Check
                                        className={`mr-2 h-4 w-4 ${formData.cliente_id === c.id ? "opacity-100" : "opacity-0"}`}
                                      />
                                      <div className="flex flex-col">
                                        <span className="font-medium">{c.nombre || c.name}</span>
                                        <span className="text-xs text-slate-500">
                                          {c.cedula || c.rnc || "Sin cédula"}
                                          {c.telefono ? ` · ${c.telefono}` : ""}
                                          {c.email ? ` · ${c.email}` : ""}
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ));
                                })()}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <p className="text-[11px] text-slate-500 mt-1">{clientes.length} cliente(s) cargado(s). Filtra escribiendo nombre, cédula, teléfono o email.</p>
                    </div>
                    <div>
                      <Label>Nombre del Cliente *</Label>
                      <Input
                        value={formData.cliente_nombre}
                        onChange={(e) => setFormData({...formData, cliente_nombre: e.target.value})}
                        placeholder="Nombre completo"
                      />
                    </div>
                    <div>
                      <Label>Cédula/RNC</Label>
                      <Input
                        value={formData.cliente_cedula}
                        onChange={(e) => setFormData({...formData, cliente_cedula: e.target.value})}
                        placeholder="000-0000000-0"
                      />
                    </div>
                    <div>
                      <Label>Teléfono</Label>
                      <Input
                        value={formData.cliente_telefono}
                        onChange={(e) => setFormData({...formData, cliente_telefono: e.target.value})}
                        placeholder="809-000-0000"
                      />
                    </div>
                    <div>
                      <Label>Celular</Label>
                      <Input
                        value={formData.cliente_celular}
                        onChange={(e) => setFormData({...formData, cliente_celular: e.target.value})}
                        placeholder="809-000-0000"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.cliente_email}
                        onChange={(e) => setFormData({...formData, cliente_email: e.target.value})}
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                    <div>
                      <Label>Ciudad</Label>
                      <Select value={formData.cliente_ciudad || undefined} onValueChange={(v) => setFormData({...formData, cliente_ciudad: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar ciudad" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.ciudades?.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Dirección</Label>
                      <Input
                        value={formData.cliente_direccion}
                        onChange={(e) => setFormData({...formData, cliente_direccion: e.target.value})}
                        placeholder="Dirección completa"
                      />
                    </div>
                    <div>
                      <Label>Póliza</Label>
                      <Input
                        value={formData.poliza}
                        onChange={(e) => setFormData({...formData, poliza: e.target.value})}
                        placeholder="Número de póliza"
                      />
                    </div>
                  </div>
                </div>

                {/* Call Data */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <PhoneCall className="w-4 h-4" />
                    Datos de la Llamada
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fecha y Hora</Label>
                      <Input
                        type="datetime-local"
                        value={formData.fecha_llamada}
                        onChange={(e) => setFormData({...formData, fecha_llamada: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Resultado *</Label>
                      <Select value={formData.resultado || undefined} onValueChange={(v) => setFormData({...formData, resultado: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar resultado" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.resultados?.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Próxima Acción</Label>
                      <Select value={formData.proxima_accion || undefined} onValueChange={(v) => setFormData({...formData, proxima_accion: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar acción" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.proximas_acciones?.map((a) => (
                            <SelectItem key={a} value={a}>{a}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fecha Próxima Acción</Label>
                      <Input
                        type="datetime-local"
                        value={formData.fecha_proxima_accion}
                        onChange={(e) => setFormData({...formData, fecha_proxima_accion: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Validations */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Datos Validados
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {config?.tipos_validacion?.filter(t => t.activo).map((tipo) => (
                      <div key={tipo.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={tipo.id}
                          checked={formData.validaciones.includes(tipo.id)}
                          onCheckedChange={(checked) => handleValidacionCheck(tipo.id, tipo.label, checked)}
                        />
                        <Label htmlFor={tipo.id} className="cursor-pointer">{tipo.label}</Label>
                      </div>
                    ))}
                  </div>

                  {/* Validation Prompt Inline */}
                  {validacionPrompt && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3" data-testid="validacion-prompt">
                      <p className="font-medium text-blue-900 dark:text-blue-200 text-sm">
                        {validacionPrompt.step === "input" ? validacionPrompt.inputLabel : validacionPrompt.question}
                      </p>
                      {validacionPrompt.step === "initial" ? (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handlePromptResponse("si")} className="bg-green-600 hover:bg-green-700 text-white">
                            Sí
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handlePromptResponse("no")}>
                            No
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={promptInput}
                            onChange={(e) => setPromptInput(e.target.value)}
                            placeholder={`Nuevo ${validacionPrompt.label}...`}
                            className="flex-1"
                            autoFocus
                            data-testid="prompt-input"
                            onKeyDown={(e) => { if (e.key === "Enter") handlePromptResponse("submit"); }}
                          />
                          <Button size="sm" onClick={() => handlePromptResponse("submit")} className="bg-blue-600 hover:bg-blue-700 text-white">
                            Guardar
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setValidacionPrompt(null)}>
                            Cancelar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Datos a Actualizar (obligatorio) */}
                <div className="space-y-2">
                  <Label>Datos a Actualizar *</Label>
                  <Textarea
                    value={formData.datos_actualizar}
                    onChange={(e) => setFormData({...formData, datos_actualizar: e.target.value})}
                    placeholder="Información recopilada de las validaciones..."
                    rows={4}
                    className={!formData.datos_actualizar?.trim() ? "border-red-300" : ""}
                    data-testid="datos-actualizar"
                  />
                </div>

                {/* Observaciones */}
                <div className="space-y-2">
                  <Label>Observaciones</Label>
                  <Textarea
                    value={formData.observaciones}
                    onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                    placeholder="Notas adicionales sobre la llamada..."
                    rows={3}
                    data-testid="observaciones"
                  />
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowNewDialog(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>
                {editMode ? "Guardar Cambios" : "Registrar Llamada"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Detalles de la Llamada
              </DialogTitle>
            </DialogHeader>
            {selectedLlamada && (
              <div className="space-y-4">
                <ClienteAtencionWatcher nombre={selectedLlamada.cliente_nombre} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Cliente</Label>
                    <p className="font-medium">{selectedLlamada.cliente_nombre}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Cédula/RNC</Label>
                    <p className="font-medium">{selectedLlamada.cliente_cedula || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Teléfono</Label>
                    <p className="font-medium">{selectedLlamada.cliente_telefono || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Celular</Label>
                    <p className="font-medium">{selectedLlamada.cliente_celular || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p className="font-medium">{selectedLlamada.cliente_email || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Ciudad</Label>
                    <p className="font-medium">{selectedLlamada.cliente_ciudad || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Dirección</Label>
                    <p className="font-medium">{selectedLlamada.cliente_direccion || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Póliza</Label>
                    <p className="font-medium">{selectedLlamada.poliza || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Fecha de Llamada</Label>
                    <p className="font-medium">
                      {selectedLlamada.fecha_llamada ? new Date(selectedLlamada.fecha_llamada).toLocaleString('es-DO') : "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Resultado</Label>
                    <Badge className={getResultadoColor(selectedLlamada.resultado)}>
                      {selectedLlamada.resultado}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Próxima Acción</Label>
                    <p className="font-medium">{selectedLlamada.proxima_accion || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Datos Validados</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedLlamada.validaciones?.map((v) => (
                        <Badge key={v} variant="outline" className="capitalize">{v}</Badge>
                      ))}
                      {(!selectedLlamada.validaciones || selectedLlamada.validaciones.length === 0) && (
                        <span className="text-slate-400">Ninguno</span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Datos a Actualizar</Label>
                    <p className="font-medium whitespace-pre-line">{selectedLlamada.datos_actualizar || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Observaciones</Label>
                    <p className="font-medium whitespace-pre-line">{selectedLlamada.observaciones || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Registrado por</Label>
                    <p className="font-medium">{selectedLlamada.usuario_nombre}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Fecha de Registro</Label>
                    <p className="font-medium">
                      {selectedLlamada.created_at ? new Date(selectedLlamada.created_at).toLocaleString('es-DO') : "-"}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Cerrar
              </Button>
              <Button onClick={() => { setShowViewDialog(false); openEditDialog(selectedLlamada); }}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
